import React, { useState, useContext, useEffect, useCallback, memo, startTransition, ViewTransition } from "react";
import { Temporal } from "@js-temporal/polyfill";

import { Dashboard, Delivery, DispatchTask, Driver, IDispatchClient } from "./model";
import { useDashboardConfig } from "./dispatch_api_client";
import { CurrentTimeContext } from "./delivery";
import { createDriverNote } from "./driver"
import { adminChatsPath } from "src/routes";

// a DispatchTask, after resolving the deliveryId to a Delivery
interface TaskWithDelivery {
  id: number;
  delivery: Delivery;
  taskType: string;
  createdAt: Temporal.ZonedDateTime;
  readyAt: Temporal.ZonedDateTime;
}

const TaskListSubheading = ({
  title,
  count,
} : {
  title: string,
  count: number,
}): React.JSX.Element => {
  return (
    <ViewTransition key={title} update="stop-subheading-stretching">
      <div className="subheading">
        <span className="text">{title}</span>
        <span className="count">{count}</span>
      </div>
    </ViewTransition>
  );
}

const TaskList = ({
  title,
  count,
  children
} : {
  title: string,
  count: number,
  children: React.ReactNode,
}): React.JSX.Element => {
  return (
    <ViewTransition key={title} default="gentle-speed">
      {count == 0 ? <></> :
        <div className="task-group">
          <TaskListSubheading title={title} count={count} />

          <div className="task-list">
            {children}
          </div>
        </div>}
    </ViewTransition>
  );
}

// Each task is a particular situation for a particular Delivery. We don't want to present at that fine a level;
// we roll up to a level that becomes the subject of the dispatcher's required action, for example the driver if
// the dispatcher needs to nudge the driver, or the store if the dispatcher needs to call the store.
function filterAndGroupTasks<K>(
  tasks: TaskWithDelivery[] | undefined,
  callbackFn: (task: TaskWithDelivery) => K | undefined | null,
): Map<K, TaskWithDelivery[]> {
  return (tasks ?? []).reduce(
    (results, task) => {
      const key = callbackFn(task);
      if (!key) return results;

      const values = results.get(key);
      if (values) {
        values.push(task);
      } else {
        results.set(key, [task]);
      }
      return results;
    },
    new Map<K, TaskWithDelivery[]>());
}

function secondsToHHMMSS(seconds: number) {
  const hours = Math.floor(seconds/3600);
  const mins = Math.floor(seconds/60) % 60;
  const secs = Math.floor(seconds) % 60;
  if (hours == 0) {
    return `${mins}:${String(secs).padStart(2, '0')}`;
  } else {
    return `${hours}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }
}

const TaskCardHeader = ({
  tasks,
  children,
} : {
  tasks: TaskWithDelivery[],
  children: React.ReactNode,
}) => {
  const currentTime = useContext(CurrentTimeContext);
  const firstCreatedAt = tasks.map((task) => task.createdAt).reduce((min, time) => Temporal.ZonedDateTime.compare(time, min) > 0 ? min : time);
  const firstReadyAt = tasks.map((task) => task.readyAt).reduce((min, time) => Temporal.ZonedDateTime.compare(time, min) > 0 ? min : time);
  const elapsedSeconds = (currentTime.getTime() - firstCreatedAt.epochMilliseconds)/1000;
  const readySeconds = (currentTime.getTime() - firstReadyAt.epochMilliseconds)/1000;

  return <>
    <div className="header">
      <div className="heading">
        {children}
      </div>
      <div className={`time ${readySeconds > 300 ? 'unresolved-for-five-minutes' : ''}`}>
        {secondsToHHMMSS(elapsedSeconds)}
      </div>
    </div>
  </>;
}

function pickupTime(delivery: Delivery) {
  return <>
    <div title={delivery.readyToCollect ? "Pickup time (ready for pickup!)" : "Pickup time"} className={delivery.readyToCollect ? 'pickup-time ready-for-pickup' : 'pickup-time'}>
      <span className="icon is-small has-text-grey">
        <i className="far fa-sm fa-clock"></i>
      </span>
      {delivery.pickupTime.toUpperCase()}
    </div>
  </>;
}

function docketDescription(delivery: Delivery) {
  return (
    <div key={delivery.id} className="item">
      <div className="description">
        <a href={delivery.href} title="Open order" target="_blank" rel="noreferrer" className={delivery.operationsException ? 'operations-exception' : ''}>
          <span className="reference">{delivery.docketReference}</span>
          <span className="icon is-small ml-1">
            <i className="fa fa-arrow-up-right-from-square"></i>
          </span>
        </a>
      </div>
      {pickupTime(delivery)}
    </div>
  );
}

const TaskCardDockets = ({
  tasks,
  suffix,
} : {
  tasks: TaskWithDelivery[],
  suffix: string,
}) => {
  if (tasks.length == 1) {
    return <>
      <div className="task-description is-single">
        {docketDescription(tasks[0].delivery)}
      </div>
    </>;
  } else {
    return <>
      <details className="task-description is-multiple">
        <summary><span className="count">{tasks.length} orders</span> {suffix}</summary>
        {tasks.map((task) => docketDescription(task.delivery))}
      </details>
    </>;
  }
}

function deliveryDescription(delivery: Delivery) {
  return (
    <div key={delivery.id} className="item">
      <div className="description">
        <div className="pickup-from">
          {delivery.storeName}
        </div>
        <div className="deliver-to">
          <span className="icon is-small">
            <i className="fas fa-xs fa-arrow-right"></i>
          </span>
          <span className="destination">{delivery.suburb}</span>
        </div>
      </div>
      {pickupTime(delivery)}
    </div>
  );
}

const TaskCardDeliveries = ({
  tasks,
  suffix,
} : {
  tasks: TaskWithDelivery[],
  suffix: string,
}) => {
  if (tasks.length == 1) {
    return <>
      <details className="task-description is-single">
        <summary>{deliveryDescription(tasks[0].delivery)}</summary>
      </details>
    </>;
  } else {
    return <>
      <details className="task-description is-multiple">
        <summary><span className="count">{tasks.length} deliveries</span> {suffix}</summary>
        {tasks.map((task) => deliveryDescription(task.delivery))}
      </details>
    </>;
  }
}

const CallButton = ({
  phoneNumber,
  children,
} : {
  phoneNumber: string | undefined,
  children: React.ReactNode,
}) => {
  return phoneNumber == undefined || phoneNumber == '' ? (
      <button className="button is-small is-primary" disabled>
        <span className="icon is-small">
          <i className="fas fa-phone"></i>
        </span>
        <span>{children}</span>
      </button>
    ) : (
      <a href={`aircall://${phoneNumber.replaceAll(/\s+/g, "")}`} title={`Call ${phoneNumber}`}>
        <button className="button is-small is-primary">
          <span className="icon is-small">
            <i className="fas fa-phone"></i>
          </span>
          <span>{children}</span>
        </button>
      </a>
    );
}

const StoreCard = ({
  storeId,
  tasks,
  suffix,
} : {
  storeId: number,
  tasks: TaskWithDelivery[],
  suffix: string,
}) => {
  // stores aren't first-class objects on the dashboard; we basically have to extract them from the fields on Delivery
  // at present the fields of interest here will be the same for all deliveries from the store (unless it literally got
  // edited between delivery broadcasts), so not bothering to check the other tasks.
  const store = { name: tasks[0].delivery.storeName, phone: tasks[0].delivery.storePhone, href: tasks[0].delivery.storeHref };

  return (
    <ViewTransition key={storeId} default="gentle-speed">
      <div key={storeId} className={`task ${tasks[0].taskType}`}>
        <TaskCardHeader tasks={tasks}>
          {store.name}
        </TaskCardHeader>

        <TaskCardDockets tasks={tasks} suffix={suffix} />

        <div className="actions">
          <CallButton phoneNumber={store.phone}>
            Call store
          </CallButton>

          <a href={store.href} title="Open store" target="_blank" rel="noreferrer" className="button is-small is-outlined">
            <span className="icon is-small">
              <i className="fa fa-arrow-up-right-from-square"></i>
            </span>
          </a>
        </div>
      </div>
    </ViewTransition>
  );
}

const StoreTaskList = ({
  title,
  tasksByStore,
  suffix,
} : {
  title: string,
  tasksByStore: Map<number, TaskWithDelivery[]>,
  suffix: string,
}) => {
  return <>
    <TaskList key={title} title={title} count={tasksByStore.size}>
      {Array.from(tasksByStore.entries(), ([storeId, storeTasks]) => <StoreCard key={storeId} storeId={storeId} tasks={storeTasks} suffix={suffix} />)}
    </TaskList>
  </>;
}

const jumpToDriver = (driverId: number) => {
  const element = document.getElementById(`driver_${driverId}`);
  if (element) {
    element.scrollIntoView(false);
    element.classList.add("pulse");
    const finished = () => {
      element.classList.remove("pulse")
      element.removeEventListener("animationend", finished);
    }
    element.addEventListener("animationend", finished);
  }
}

function deliveryIdentifiers(tasks: TaskWithDelivery[]): string {
  return tasks.map((task) => `${task.delivery.docketReference ?? task.delivery.bookingIdentifier} (#${task.delivery.orderId})`).join(' / ');
}

function draftDriverNote(taskType: string, tasks: TaskWithDelivery[]): string | undefined {
  switch (taskType) {
  case 'waiting_at_store':
    return `Wait time on ${deliveryIdentifiers(tasks)}`;
  }
}

const DriverCard = ({
  driverId,
  driver,
  tasks,
  suffix,
  areaSlug,
  apiClient,
} : {
  driverId: number,
  driver: Driver | undefined,
  tasks: TaskWithDelivery[],
  suffix: string,
  areaSlug: string,
  apiClient: IDispatchClient,
}) => {
  // Different types of tasks will always be on different cards
  const taskType = tasks[0].taskType;

  return (
    <ViewTransition key={driverId} default="gentle-speed">
      <div key={driverId} className={`task ${taskType}`}>
        <TaskCardHeader tasks={tasks}>
          {driver?.name ?? 'Unknown driver!'}
        </TaskCardHeader>

        <TaskCardDeliveries tasks={tasks} suffix={suffix} />

        <div className="actions">
          <CallButton phoneNumber={driver?.mobileNumber}>
            Call driver
          </CallButton>

          <a href={`${adminChatsPath()}/dispatcher?area[]=${areaSlug}&focus=${driverId}`} title="Chat" target="_blank" rel="noreferrer" className="button is-small is-outlined">
            <span className="icon">
              <i className="far fa-message"></i>
            </span>
          </a>

          <button onClick={() => createDriverNote(apiClient, driverId, draftDriverNote(taskType, tasks))} className="button is-small is-outlined" title="Add driver note">
            <span className="icon is-small">
              <i className="far fa-file-pen"></i>
            </span>
          </button>

          <button onClick={() => jumpToDriver(driverId)} className="button is-small is-outlined" title="Jump to driver">
            <span className="icon is-small">
              <i className="far fa-square-user"></i>
            </span>
          </button>
        </div>
      </div>
    </ViewTransition>
  );
}

const DriverTaskList = ({
  title,
  tasksByDriver,
  suffix,
  dashboard,
  apiClient,
} : {
  title: string,
  tasksByDriver: Map<number, TaskWithDelivery[]>,
  suffix: string,
  dashboard: Dashboard,
  apiClient: IDispatchClient,
}) => {
  return <>
    <TaskList key={title} title={title} count={tasksByDriver.size}>
      {Array.from(tasksByDriver.entries(), ([driverId, driverTasks]) => <DriverCard key={driverId} driverId={driverId} driver={dashboard.driversById().get(driverId)} tasks={driverTasks} suffix={suffix} areaSlug={dashboard.areaSlug} apiClient={apiClient} />)}
    </TaskList>
  </>;
}

function hydrateTasks(dispatchTasks: DispatchTask[], deliveries: Map<string, Delivery>): TaskWithDelivery[] {
  // To make the broadcasts independent, DispatchTask only refers to deliveries by ID. We need to use the actual
  // Delivery objects, so hydrate the task objects here.
  const tasks: TaskWithDelivery[] = [];

  for (const dispatchTask of dispatchTasks) {
    const delivery = deliveries.get(dispatchTask.deliveryId);
    if (!delivery) continue;

    tasks.push({
      id: dispatchTask.id,
      delivery: delivery,
      taskType: dispatchTask.taskType,
      createdAt: dispatchTask.createdAt,
      readyAt: dispatchTask.readyAt,
    });
  }

  return tasks;
}

function groupTasks(tasks: TaskWithDelivery[]): Map<string, TaskWithDelivery[]> {
  const tasksByType = new Map<string, TaskWithDelivery[]>();

  for (const task of tasks) {
    const tasksForType = tasksByType.get(task.taskType);

    if (tasksForType) {
      tasksForType.push(task);
    } else {
      tasksByType.set(task.taskType, [task]);
    }
  }

  return tasksByType;
}

const TaskStrip = memo(function TaskStrip() {
  const { apiClient } = useDashboardConfig();
  const [ dashboard, setDashboard ] = useState(Dashboard.emptyDashboard());

  const handleDashboardUpdated = useCallback((newDashboard: Dashboard) => {
    startTransition(() => {
      setDashboard(newDashboard);
    });
  }, [setDashboard]);

  useEffect(() => {
    apiClient.dashboardUpdated.addListener(handleDashboardUpdated);
    return () => { apiClient.dashboardUpdated.removeListener(handleDashboardUpdated); };
  }, [apiClient, handleDashboardUpdated]);

  // Dashboard's CurrentTimeProvider has a 5s tick, to reduce the overhead of re-rendering all the bits of the dash that
  // only need low-resolution time changes. We display the actual seconds, so use a 1s tick.
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const intervalTimer = window.setInterval(() => { setNow(new Date()); }, 500);
    return () => { clearInterval(intervalTimer); };
  });

  if (!dashboard.tasks) return <></>;
  const tasksWithDeliveries = hydrateTasks(dashboard.tasks, dashboard.deliveriesById());
  const tasksByType = groupTasks(tasksWithDeliveries);

  const docketCancelledByStoreTasks = filterAndGroupTasks<number>(tasksByType.get('docket_cancelled_by_store'), (task) => task.delivery.storeId);
  const docketNotAcknowledgedTasks = filterAndGroupTasks<number>(tasksByType.get('docket_not_acknowledged'), (task) => task.delivery.storeId);
  const deliveryNotAcceptedTasks = filterAndGroupTasks<number>(tasksByType.get('delivery_not_accepted'), (task) => task.delivery.driverId);
  const driverWaitingAtStoreTasks = filterAndGroupTasks<number>(tasksByType.get('waiting_at_store'), (task) => task.delivery.driverId);

  return <>
    <div className="dispatch-dashboard-tasks">
      <div className="heading">
        <span className="text">Tasks</span>
        <span className="count">{docketNotAcknowledgedTasks.size + deliveryNotAcceptedTasks.size + driverWaitingAtStoreTasks.size}</span>
      </div>
      <div className="task-groups">
        <CurrentTimeContext.Provider value={now}>
          <StoreTaskList key="docket_cancelled_by_store" title="Store cancelled via integration" tasksByStore={docketCancelledByStoreTasks} suffix="cancelled" />
          <StoreTaskList key="docket_not_acknowledged" title="Store hasn't acknowledged" tasksByStore={docketNotAcknowledgedTasks} suffix="unacknowledged" />
          <DriverTaskList key="delivery_not_accepted" title="Not accepted by driver" tasksByDriver={deliveryNotAcceptedTasks} suffix="not accepted" apiClient={apiClient} dashboard={dashboard} />
          <DriverTaskList key="waiting_at_store" title="Waiting at store" tasksByDriver={driverWaitingAtStoreTasks} suffix="to collect" apiClient={apiClient} dashboard={dashboard} />
        </CurrentTimeContext.Provider>
      </div>
    </div>
  </>;
});

export { TaskStrip };
